import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { TableBaseAbstractComponent } from '../../../../shared/components/table-base-abstract.component';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { TableRequestData } from '../../../../shared/models/table/table-request-param';
import { OrganizacjaApi } from '../../../../shared/services/api/organizacja.api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Paginator } from 'primeng/paginator';
import { Table, TableModule } from 'primeng/table';
import { DialogService } from 'primeng/dynamicdialog';
import { OrganizationSettingsEditMemberComponent } from '../organization-settings-edit-member/organization-settings-edit-member.component';
import { OrganizationSettingsAddMemberComponent } from '../organization-settings-create-member/organization-settings-add-member.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { PreviewDataWrapperComponent } from '../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { TableResponse } from '../../../../shared/models/table-response';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConstLabelReaderPipe } from '../../../../shared/pipes/const-label-reader.pipe';
import {
  ROLA_REPREZNTANTA_ORGANIZACJI_LABELS,
  RolaReprezentantaOrganizacji,
} from '../../../../shared/enums/rola-reprezentanta-organizacji.enum';
import { ReprezentantOrganizacji } from '../../../../shared/models/reprezentant-organizacji';
import { SpinnerService } from '../../../../core/services/spinner.service';
import {
  OrganizationRepresentativeUpdateRoleReq,
  ZaproszenieDoOrganizacji,
} from '../../../../shared/models/custom-types';
import { Zalacznik } from '../../../../shared/models/zalacznik';
import { TooltipModule } from 'primeng/tooltip';
import { HttpUtils } from '../../../../shared/utils/http.utils';
import { AuthService } from '../../../../core/services/auth.service';
import { SortDirection } from '../../../../shared/enums/sort-direction.enum';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';
import { Router } from '@angular/router';
import { Beneficjent } from '../../../../shared/models/beneficjent';
import { APP_PATHS } from '../../../../core/config/app-paths';
import { AuthApi } from '../../../../shared/services/api/auth.api';

@Component({
  selector: 'app-organization-settings-representatives-table',
  imports: [
    Button,
    Card,
    Paginator,
    TableModule,
    PreviewDataWrapperComponent,
    ConstLabelReaderPipe,
    TooltipModule,
  ],
  templateUrl: './organization-settings-representatives-table.component.html',
  styleUrl: './organization-settings-representatives-table.component.scss',
  providers: [OrganizacjaApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSettingsRepresentativesTableComponent extends TableBaseAbstractComponent<ReprezentantOrganizacji> {
  @ViewChild('table') override tableRef!: Table;

  protected readonly ROLA_REPREZNTANTA_ORGANIZACJI_LABELS =
    ROLA_REPREZNTANTA_ORGANIZACJI_LABELS;
  protected readonly RolaReprezentantaOrganizacji =
    RolaReprezentantaOrganizacji;

  readonly #spinnerService = inject(SpinnerService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #organizationApi = inject(OrganizacjaApi);
  readonly #dialogService = inject(DialogService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #authService = inject(AuthService);
  readonly #router = inject(Router);
  readonly #loggedUser = signal<Beneficjent | null>(null);
  readonly #authApi = inject(AuthApi);

  readonly #currentUserRole = computed(() => {
    const pesel = this.#authService.getUser()?.pesel ?? null;
    const data = this.rows();

    if (!pesel || !data?.length) return null;

    const user = data.find((r) => r.pesel === pesel);
    return user?.rola ?? null;
  });

  protected readonly userHasPlenipotentiaryRole = computed(
    () => this.#currentUserRole() === RolaReprezentantaOrganizacji.OUP,
  );

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<ReprezentantOrganizacji>> => {
    params.sortBy = !params.sortBy ? 'imie' : params.sortBy;
    params.order = !params.order ? SortDirection.ASC : params.order;
    return this.#organizationApi.getOrganizationRepresentatives$(params);
  };

  protected openDeleteRepresentativeDialog(
    representative: ReprezentantOrganizacji,
  ): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz usunąć użytkownika: ${representative.imie} ${representative.nazwisko}`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#deleteRepresentative(representative)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected openEditMemberModal(member: ReprezentantOrganizacji): void {
    const onDeleteAttachmentSubject = new Subject<number>();
    const onAddAttachmentSubject = new Subject<Zalacznik>();
    const ref = this.#dialogService.open(
      OrganizationSettingsEditMemberComponent,
      {
        closable: true,
        header: `Edycja uprawnień użytkownika: ${member.imie} ${member.nazwisko}`,
        closeOnEscape: true,
        modal: true,
        dismissableMask: true,

        inputValues: {
          ['organizationRepresentative']: member,
          ['onDeleteAttachmentSubject']: onDeleteAttachmentSubject,
          ['onAddAttachmentSubject']: onAddAttachmentSubject,
        },
      },
    );

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap((response: OrganizationRepresentativeUpdateRoleReq) =>
          this.#updateOrganizationRepresentativeRole(response),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    onAddAttachmentSubject
      .pipe(
        tap((zalacznik: Zalacznik) => member.zalaczniki?.push(zalacznik)),
        takeUntil(ref?.onClose!),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    onDeleteAttachmentSubject
      .pipe(
        tap((idPelnomocnictwa: number) => {
          member.zalaczniki = (member.zalaczniki ?? []).filter(
            (z) => z.id !== idPelnomocnictwa,
          );
        }),
        takeUntil(ref?.onClose!),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected openAddMemberModal(): void {
    const ref = this.#dialogService.open(
      OrganizationSettingsAddMemberComponent,
      {
        closable: true,
        header: `Dodaj użytkownika`,
        closeOnEscape: true,
        modal: true,
        dismissableMask: true,
        width: '45vw',
        breakpoints: {
          '960px': '90vw',
          '640px': '90vw',
        },
      },
    );

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap((zaproszenie: ZaproszenieDoOrganizacji) =>
          this.#addMember(zaproszenie),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #addMember(daneZaproszenia: ZaproszenieDoOrganizacji): void {
    this.#spinnerService.show();
    this.#organizationApi
      .inviteRepresentative$(daneZaproszenia)
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Użytkownik został zaproszony pomyślnie',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas zapraszania użytkownika',
          );
          return EMPTY;
        }),

        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.#spinnerService.hide()),
      )
      .subscribe();
  }

  protected downloadAllAttachments(rep: ReprezentantOrganizacji): void {
    const attachments = rep.zalaczniki ?? [];

    if (!attachments.length) {
      return;
    }

    const ids = attachments.map((a) => a.id!);

    this.#spinnerService.show();

    this.#organizationApi
      .downloadRepresentativePowers$(ids)
      .pipe(
        tap((resp) => {
          HttpUtils.downloadFile(resp);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się pobrać pełnomocnictw',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #deleteRepresentative(representative: ReprezentantOrganizacji): void {
    this.#spinnerService.show();

    const isDeletingSelf =
      representative.idBeneficjenta === this.#authService.getUser()?.id;
    const isAdmin = representative.rola === RolaReprezentantaOrganizacji.ADM;

    this.#organizationApi
      .deleteOrganizationRepresentative$(representative.idBeneficjenta!)
      .pipe(
        switchMap(() => {
          if (isDeletingSelf && isAdmin) {
            return this.#authApi.getCurrentUser$().pipe(
              tap((response) => {
                const freshUser = response.body;
                if (freshUser) {
                  this.#authService.setUser(freshUser);
                }
              }),
              tap(() => {
                this.#deleteRepresentativeSuccess();
                this.#router.navigate([
                  APP_PATHS.auth.root,
                  APP_PATHS.auth.applicationContext,
                ]);
              }),
            );
          } else {
            this.#deleteRepresentativeSuccess();
            return of(null);
          }
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby usunięcia reprezentanta organizacji',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #updateOrganizationRepresentativeRole(
    response: OrganizationRepresentativeUpdateRoleReq,
  ): void {
    this.#spinnerService.show();
    this.#organizationApi
      .updateOrganizationRepresentativeRole$(response)
      .pipe(
        tap(() => this.#updateRepresentativeRoleSuccess()),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby aktualizacji uprawnień reprezentanta organizacji',
          );
          throw new Error(err);
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #deleteRepresentativeSuccess(): void {
    this.#httpResponseService.showSuccessMessage(
      'Poprawnie usunięto reprezentanta organizacji',
    );
    this.reloadData();
  }

  #updateRepresentativeRoleSuccess(): void {
    this.#httpResponseService.showSuccessMessage(
      'Poprawnie zaktualizowano uprawnienia reprezentanta organizacji',
    );
    this.reloadData();
  }
}
